import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit2, Trash2, ExternalLink, Copy, Search, Globe, Target, Dumbbell, Trophy, ToggleLeft, ToggleRight, Sun, Moon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import AdminLayout from '../../components/AdminLayout';
import api from '../../utils/api';
import { toast } from 'sonner';

const AdminLandingPages = () => {
  const [landingPages, setLandingPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingPage, setDeletingPage] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    headline: '',
    subheadline: '',
    banner_headline: '',
    banner_subtitle: '',
    flow_template: 'AMATEUR_SPORTS_V1',
    cta_text: 'Get Your Quote',
    hero_image_url: '',
    utm_source: '',
    utm_campaign: '',
    active: true,
    theme: 'light'
  });

  useEffect(() => {
    loadLandingPages();
  }, []);

  const loadLandingPages = async () => {
    try {
      const response = await api.get('/landing-pages');
      setLandingPages(response.data);
    } catch (error) {
      toast.error('Failed to load landing pages');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      headline: '',
      subheadline: '',
      banner_headline: '',
      banner_subtitle: '',
      flow_template: 'AMATEUR_SPORTS_V1',
      cta_text: 'Get Your Quote',
      hero_image_url: '',
      utm_source: '',
      utm_campaign: '',
      active: true,
      theme: 'light'
    });
    setEditingPage(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleOpenEdit = (page) => {
    setFormData({
      name: page.name || '',
      slug: page.slug || '',
      headline: page.headline || '',
      subheadline: page.subheadline || '',
      banner_headline: page.banner_headline || '',
      banner_subtitle: page.banner_subtitle || '',
      flow_template: page.flow_template || 'AMATEUR_SPORTS_V1',
      cta_text: page.cta_text || 'Get Your Quote',
      hero_image_url: page.hero_image_url || '',
      utm_source: page.utm_source || '',
      utm_campaign: page.utm_campaign || '',
      active: page.active !== false,
      theme: page.theme || 'light'
    });
    setEditingPage(page);
    setDialogOpen(true);
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleNameChange = (value) => {
    setFormData(prev => ({
      ...prev,
      name: value,
      slug: editingPage ? prev.slug : generateSlug(value)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.slug || !formData.headline) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingPage) {
        await api.put(`/landing-pages/${editingPage.id}`, formData);
        toast.success('Landing page updated successfully');
      } else {
        await api.post('/landing-pages', formData);
        toast.success('Landing page created successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadLandingPages();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save landing page';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingPage) return;
    
    try {
      await api.delete(`/landing-pages/${deletingPage.id}`);
      toast.success('Landing page deleted');
      setDeleteDialogOpen(false);
      setDeletingPage(null);
      loadLandingPages();
    } catch (error) {
      toast.error('Failed to delete landing page');
    }
  };

  const copyPageUrl = (slug) => {
    const url = `${window.location.origin}/lp/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard');
  };

  const filteredPages = landingPages.filter(page =>
    page.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.slug?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.headline?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const baseUrl = window.location.origin;

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-[#1A1A1A] uppercase tracking-wide" style={{fontFamily: "Barlow Condensed, sans-serif"}}>Landing Pages</h1>
            <p className="text-sm text-[#6B7280] mt-1">
              Create Players Health landing pages for paid media campaigns and website integration
            </p>
          </div>
          <Button 
            onClick={handleOpenCreate}
            data-testid="create-landing-page-btn"
            className="bg-[#7B6FE8] hover:bg-[#6B5CE7] text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Landing Page
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#9CA3AF]" />
          <Input
            placeholder="Search landing pages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-11 border-[#E5E7EB]"
          />
        </div>

        {/* Landing Pages Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#7B6FE8]"></div>
          </div>
        ) : filteredPages.length === 0 ? (
          <Card className="bg-white border border-[#E5E7EB]">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Globe className="h-12 w-12 text-[#9CA3AF] mb-4" />
              <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">No landing pages yet</h3>
              <p className="text-[#6B7280] text-center max-w-sm mb-4">
                Create your first Players Health landing page for marketing campaigns or website integration.
              </p>
              <Button onClick={handleOpenCreate} className="bg-[#7B6FE8] hover:bg-[#6B5CE7] text-white">
                <Plus className="h-4 w-4 mr-2" />
                Create Landing Page
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPages.map((page) => (
              <Card 
                key={page.id} 
                className={`bg-white border hover:shadow-md transition-shadow ${!page.active ? 'opacity-60' : ''}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg font-semibold text-[#1A1A1A] flex items-center gap-2">
                        {page.name}
                        {!page.active && (
                          <Badge variant="outline" className="text-[#9CA3AF] border-[#E5E7EB]">Inactive</Badge>
                        )}
                      </CardTitle>
                      <p className="text-sm text-[#6B7280] mt-1 flex items-center gap-1">
                        <Globe className="h-3.5 w-3.5" />
                        /lp/{page.slug}
                      </p>
                    </div>
                    <Badge 
                      className={page.flow_template === 'BOUTIQUE_FITNESS_V1' 
                        ? 'bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]' 
                        : page.flow_template === 'BOTH_V1'
                        ? 'bg-[#FFF7ED] text-[#9A3412] border-[#FED7AA]'
                        : 'bg-[#EEF0FF] text-[#6B5CE7] border-[#C4BFFF]'}
                    >
                      {page.flow_template === 'BOUTIQUE_FITNESS_V1' ? (
                        <><Dumbbell className="h-3 w-3 mr-1" /> Fitness</>
                      ) : page.flow_template === 'BOTH_V1' ? (
                        <><Trophy className="h-3 w-3 mr-1" /> Sports & Fitness</>
                      ) : (
                        <><Trophy className="h-3 w-3 mr-1" /> Sports</>
                      )}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-[#1A1A1A] line-clamp-2">{page.headline}</p>
                    {page.subheadline && (
                      <p className="text-xs text-[#6B7280] mt-1 line-clamp-2">{page.subheadline}</p>
                    )}
                  </div>

                  {(page.utm_source || page.utm_campaign) && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {page.utm_source && (
                        <Badge variant="outline" className="text-xs bg-[#F7F8FA]">
                          <Target className="h-3 w-3 mr-1" />
                          {page.utm_source}
                        </Badge>
                      )}
                      {page.utm_campaign && (
                        <Badge variant="outline" className="text-xs bg-[#F7F8FA]">
                          {page.utm_campaign}
                        </Badge>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-2 border-t border-[#E5E7EB]">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyPageUrl(page.slug)}
                      className="flex-1 text-xs"
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Copy URL
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      asChild
                      className="flex-1 text-xs"
                    >
                      <a href={`/lp/${page.slug}`} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-3 w-3 mr-1" />
                        Preview
                      </a>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenEdit(page)}
                      className="text-xs"
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setDeletingPage(page); setDeleteDialogOpen(true); }}
                      className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#1A1A1A]">
                {editingPage ? 'Edit Landing Page' : 'Create Landing Page'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-5 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#1A1A1A] font-medium">Page Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="e.g., Spring Campaign 2026"
                    className="mt-1.5 border-[#E5E7EB]"
                    data-testid="landing-page-name-input"
                  />
                  <p className="text-xs text-[#6B7280] mt-1">Internal reference name</p>
                </div>
                <div>
                  <Label className="text-[#1A1A1A] font-medium">URL Slug *</Label>
                  <div className="flex items-center mt-1.5">
                    <span className="text-sm text-[#6B7280] bg-[#F3F4F6] border border-r-0 border-[#E5E7EB] px-3 py-2 rounded-l-md">
                      /lp/
                    </span>
                    <Input
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: generateSlug(e.target.value) })}
                      placeholder="spring-campaign"
                      className="rounded-l-none border-[#E5E7EB]"
                      data-testid="landing-page-slug-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-[#1A1A1A] font-medium">Headline *</Label>
                <Input
                  value={formData.headline}
                  onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                  placeholder="e.g., Protect Your Youth Sports Organization Today"
                  className="mt-1.5 border-[#E5E7EB]"
                  data-testid="landing-page-headline-input"
                />
              </div>

              <div>
                <Label className="text-[#1A1A1A] font-medium">Subheadline</Label>
                <Textarea
                  value={formData.subheadline}
                  onChange={(e) => setFormData({ ...formData, subheadline: e.target.value })}
                  placeholder="A brief description that appears below the headline..."
                  className="mt-1.5 border-[#E5E7EB]"
                  rows={2}
                />
              </div>

              {/* Banner Section */}
              <div className="border-t border-[#E5E7EB] pt-4">
                <Label className="text-[#1A1A1A] font-medium mb-3 block">Gradient Banner Section</Label>
                <p className="text-xs text-[#6B7280] mb-3">Customize the gradient banner that appears below the hero section.</p>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-[#6B7280]">Banner Headline</Label>
                    <Input
                      value={formData.banner_headline}
                      onChange={(e) => setFormData({ ...formData, banner_headline: e.target.value })}
                      placeholder="e.g., YOUR TRUSTED SPORTS INSURANCE PROVIDER"
                      className="mt-1 border-[#E5E7EB]"
                    />
                    <p className="text-xs text-[#9CA3AF] mt-1">Leave empty for default: "YOUR TRUSTED SPORTS INSURANCE PROVIDER"</p>
                  </div>
                  <div>
                    <Label className="text-sm text-[#6B7280]">Banner Subtitle</Label>
                    <Textarea
                      value={formData.banner_subtitle}
                      onChange={(e) => setFormData({ ...formData, banner_subtitle: e.target.value })}
                      placeholder="e.g., We know sports. We know insurance..."
                      className="mt-1 border-[#E5E7EB]"
                      rows={2}
                    />
                    <p className="text-xs text-[#9CA3AF] mt-1">Leave empty for default text</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#1A1A1A] font-medium">Application Flow</Label>
                  <Select 
                    value={formData.flow_template} 
                    onValueChange={(value) => setFormData({ ...formData, flow_template: value })}
                  >
                    <SelectTrigger className="mt-1.5 border-[#E5E7EB]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AMATEUR_SPORTS_V1">
                        <div className="flex items-center gap-2">
                          <Trophy className="h-4 w-4 text-[#7B6FE8]" />
                          Amateur Sports
                        </div>
                      </SelectItem>
                      <SelectItem value="BOUTIQUE_FITNESS_V1">
                        <div className="flex items-center gap-2">
                          <Dumbbell className="h-4 w-4 text-[#065F46]" />
                          Boutique Fitness
                        </div>
                      </SelectItem>
                      <SelectItem value="BOTH_V1">
                        <div className="flex items-center gap-2">
                          <Trophy className="h-4 w-4 text-[#7B6FE8]" />
                          Both (Sports & Fitness)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#1A1A1A] font-medium">CTA Button Text</Label>
                  <Input
                    value={formData.cta_text}
                    onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                    placeholder="Get Your Quote"
                    className="mt-1.5 border-[#E5E7EB]"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#1A1A1A] font-medium mb-2 block">Page Theme</Label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, theme: 'light' })}
                    data-testid="theme-light-btn"
                    className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 transition-all ${
                      formData.theme === 'light'
                        ? 'border-[#7B6FE8] bg-[#EEF0FF] text-[#6B5CE7]'
                        : 'border-[#E5E7EB] hover:border-[#D1D5DB] text-[#6B7280]'
                    }`}
                  >
                    <Sun className="h-4 w-4" />
                    <span className="font-medium text-sm">Light</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, theme: 'dark' })}
                    data-testid="theme-dark-btn"
                    className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 transition-all ${
                      formData.theme === 'dark'
                        ? 'border-[#7B6FE8] bg-[#232323] text-white'
                        : 'border-[#E5E7EB] hover:border-[#D1D5DB] text-[#6B7280]'
                    }`}
                  >
                    <Moon className="h-4 w-4" />
                    <span className="font-medium text-sm">Dark</span>
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-[#1A1A1A] font-medium">Hero Background Image URL (Optional)</Label>
                <Input
                  value={formData.hero_image_url}
                  onChange={(e) => setFormData({ ...formData, hero_image_url: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  className="mt-1.5 border-[#E5E7EB]"
                />
              </div>

              <div className="border-t border-[#E5E7EB] pt-4">
                <Label className="text-[#1A1A1A] font-medium mb-3 block">UTM Tracking (Optional)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-[#6B7280]">UTM Source</Label>
                    <Input
                      value={formData.utm_source}
                      onChange={(e) => setFormData({ ...formData, utm_source: e.target.value })}
                      placeholder="e.g., google, facebook"
                      className="mt-1 border-[#E5E7EB]"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-[#6B7280]">UTM Campaign</Label>
                    <Input
                      value={formData.utm_campaign}
                      onChange={(e) => setFormData({ ...formData, utm_campaign: e.target.value })}
                      placeholder="e.g., spring-2026"
                      className="mt-1 border-[#E5E7EB]"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-[#E5E7EB] pt-4">
                <div>
                  <Label className="text-[#1A1A1A] font-medium">Page Status</Label>
                  <p className="text-xs text-[#6B7280]">Inactive pages won't be accessible publicly</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setFormData({ ...formData, active: !formData.active })}
                  className={formData.active ? 'text-[#065F46]' : 'text-[#6B7280]'}
                >
                  {formData.active ? (
                    <><ToggleRight className="h-6 w-6 mr-2" /> Active</>
                  ) : (
                    <><ToggleLeft className="h-6 w-6 mr-2" /> Inactive</>
                  )}
                </Button>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={saving}
                className="bg-[#7B6FE8] hover:bg-[#6B5CE7] text-white"
              >
                {saving ? 'Saving...' : editingPage ? 'Update Page' : 'Create Page'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-[#1A1A1A]">Delete Landing Page</DialogTitle>
            </DialogHeader>
            <p className="text-[#6B7280]">
              Are you sure you want to delete <strong>{deletingPage?.name}</strong>? This action cannot be undone.
            </p>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminLandingPages;
